import { useEffect, useMemo, useState } from 'react';
import { cn } from '../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
} from '../../../styling/stylingHelper';
import { AdminLayout } from './AdminLayout';
import {
  getRolesOverview,
  setStaffRestaurantRole,
  setUserInternalRole,
  CommunicationAdmin,
  INTERNAL_ROLES,
  InternalRole,
  RolesOverview,
} from '../../../services/admin/communicationAdminService';
import { RESTAURANT_ROLES } from '../../../services/staff/staffService';

const INTERNAL_ROLE_LABELS: Record<InternalRole, string> = {
  culinary_cloud_user: 'Culinary Cloud user',
  app_only_user: 'App-only user',
};

const selectClass = cn(
  'px-2 py-1 rounded-md border text-xs',
  'bg-white dark:bg-gray-800',
  'border-gray-200 dark:border-gray-700',
  'text-gray-900 dark:text-gray-100',
  'focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-500'
);

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : 'Something went wrong';

const AdminCommunicationRolesPage = () => {
  const [overview, setOverview] = useState<RolesOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedAdminIds, setExpandedAdminIds] = useState<Set<string>>(new Set());
  const [updating, setUpdating] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const data = await getRolesOverview();
        setOverview(data);
        // Accounts that still have staff without a role are the work to do — open those first.
        setExpandedAdminIds(
          new Set(data.admins.filter((a) => a.staff.some((s) => !s.restaurantRole)).map((a) => a.id))
        );
      } catch (error) {
        console.error('Failed to load roles overview:', error);
        setLoadError(errorMessage(error));
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const internalRoleUnsetCount = useMemo(() => {
    if (!overview) return 0;
    return overview.admins.reduce(
      (sum, admin) =>
        sum + (admin.internalRole ? 0 : 1) + admin.staff.filter((s) => !s.internalRole).length,
      0
    );
  }, [overview]);

  const toggleExpanded = (adminId: string) => {
    setExpandedAdminIds((prev) => {
      const next = new Set(prev);
      if (next.has(adminId)) {
        next.delete(adminId);
      } else {
        next.add(adminId);
      }
      return next;
    });
  };

  const clearRowError = (key: string) =>
    setRowErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });

  // Applies a change to the admins tree and re-derives the coverage stat from it, so the header
  // stays in sync with the list without a refetch.
  const applyOverviewUpdate = (update: (admins: CommunicationAdmin[]) => CommunicationAdmin[]) =>
    setOverview((prev) => {
      if (!prev) return prev;
      const admins = update(prev.admins);
      const staffWithRole = admins.reduce(
        (sum, admin) => sum + admin.staff.filter((s) => s.restaurantRole).length,
        0
      );
      return { summary: { ...prev.summary, staffWithRole }, admins };
    });

  const handleRoleChange = async (staffId: string, restaurantRole: string) => {
    const key = `role-${staffId}`;
    setUpdating(key);
    clearRowError(key);
    try {
      await setStaffRestaurantRole(staffId, restaurantRole);
      applyOverviewUpdate((admins) =>
        admins.map((admin) => ({
          ...admin,
          staff: admin.staff.map((s) => (s.id === staffId ? { ...s, restaurantRole } : s)),
        }))
      );
    } catch (error) {
      console.error('Failed to update restaurant role:', error);
      setRowErrors((prev) => ({ ...prev, [key]: errorMessage(error) }));
    } finally {
      setUpdating(null);
    }
  };

  const handleInternalRoleChange = async (userId: string, internalRole: InternalRole | null) => {
    const key = `internal-${userId}`;
    setUpdating(key);
    clearRowError(key);
    try {
      await setUserInternalRole(userId, internalRole);
      applyOverviewUpdate((admins) =>
        admins.map((admin) => ({
          ...admin,
          internalRole: admin.id === userId ? internalRole : admin.internalRole,
          staff: admin.staff.map((s) => (s.id === userId ? { ...s, internalRole } : s)),
        }))
      );
    } catch (error) {
      console.error('Failed to update internal role:', error);
      setRowErrors((prev) => ({ ...prev, [key]: errorMessage(error) }));
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className={cn(secondaryText)}>Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  const totalStaff = overview?.summary.totalStaff ?? 0;
  const staffWithRole = overview?.summary.staffWithRole ?? 0;

  return (
    <AdminLayout>
      <div className="mb-6">
        <h2 className={cn(primaryText, 'text-lg', semiBoldText, 'mb-2')}>Roles</h2>
        <p className={cn(secondaryText, smallTextSize)}>
          Every account and its staff (demo accounts excluded). Setting a restaurant role here is a real
          change — it also rewrites which revenue sources that staff member can see, exactly like the
          staff card in the app. The internal role is super-admin only and never visible to customers.
        </p>
      </div>

      {loadError && (
        <div
          className={cn(
            cardClass,
            'p-4 mb-6 border border-red-200 dark:border-red-900/50',
            smallTextSize,
            'text-red-600 dark:text-red-400'
          )}
        >
          {loadError}
        </div>
      )}

      {/* Header stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className={cn(cardClass, 'p-4')}>
          <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Staff with a role</div>
          <div className={cn(primaryText, 'text-2xl', semiBoldText)}>
            {staffWithRole}/{totalStaff}
          </div>
          <div className={cn(secondaryText, 'text-xs')}>
            {totalStaff > 0 ? Math.round((staffWithRole / totalStaff) * 100) : 0}% coverage
          </div>
        </div>
        <div className={cn(cardClass, 'p-4')}>
          <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Staff without a role</div>
          <div
            className={cn(
              'text-2xl',
              semiBoldText,
              totalStaff - staffWithRole > 0
                ? 'text-red-600 dark:text-red-400'
                : 'text-green-600 dark:text-green-400'
            )}
          >
            {totalStaff - staffWithRole}
          </div>
        </div>
        <div className={cn(cardClass, 'p-4')}>
          <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Internal role not set</div>
          <div className={cn(primaryText, 'text-2xl', semiBoldText)}>{internalRoleUnsetCount}</div>
          <div className={cn(secondaryText, 'text-xs')}>admins + staff</div>
        </div>
      </div>

      <div className="space-y-3">
        {!overview || overview.admins.length === 0 ? (
          <div className={cn(cardClass, 'p-8 text-center')}>
            <p className={secondaryText}>No accounts found</p>
          </div>
        ) : (
          overview.admins.map((admin) => {
            const isExpanded = expandedAdminIds.has(admin.id);
            const missingRoleCount = admin.staff.filter((s) => !s.restaurantRole).length;
            const adminInternalKey = `internal-${admin.id}`;
            return (
              <div key={admin.id} className={cn(cardClass, 'overflow-hidden')}>
                {/* Admin header */}
                <div className="px-4 py-3 flex items-center justify-between gap-3">
                  <button
                    onClick={() => toggleExpanded(admin.id)}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    <div
                      className={cn(
                        'w-8 h-8 shrink-0 rounded-full flex items-center justify-center',
                        'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
                        smallTextSize,
                        semiBoldText
                      )}
                    >
                      {(admin.name || admin.email)[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className={cn(primaryText, smallTextSize, semiBoldText, 'truncate')}>
                        {admin.name || admin.email}
                      </div>
                      <div className={cn(secondaryText, 'text-xs truncate')}>{admin.email}</div>
                    </div>
                    <svg
                      className={cn(
                        'w-4 h-4 shrink-0 text-gray-400 dark:text-textQuaternary-dark transition-transform',
                        isExpanded && 'rotate-180'
                      )}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                      Owner
                    </span>
                    {missingRoleCount > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                        {missingRoleCount} without role
                      </span>
                    )}
                    <span className={cn(secondaryText, 'text-xs')}>
                      {admin.staff.length} staff
                    </span>
                    <InternalRoleSelect
                      value={admin.internalRole}
                      disabled={updating === adminInternalKey}
                      onChange={(next) => handleInternalRoleChange(admin.id, next)}
                    />
                  </div>
                </div>
                {rowErrors[adminInternalKey] && (
                  <div className={cn('px-4 pb-2 text-xs', 'text-red-600 dark:text-red-400')}>
                    {rowErrors[adminInternalKey]}
                  </div>
                )}

                {/* Staff */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700">
                    {admin.staff.length === 0 ? (
                      <p className={cn(secondaryText, 'text-xs mt-4')}>No staff.</p>
                    ) : (
                      <div className="mt-4 space-y-2">
                        {admin.staff.map((staff) => {
                          const roleKey = `role-${staff.id}`;
                          const internalKey = `internal-${staff.id}`;
                          return (
                            <div
                              key={staff.id}
                              className="rounded-lg border border-gray-100 dark:border-gray-700 p-3"
                            >
                              <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="min-w-0">
                                  <div className={cn(primaryText, 'text-xs', semiBoldText)}>
                                    {staff.name || staff.email}
                                  </div>
                                  <div className={cn(secondaryText, 'text-xs')}>{staff.email}</div>
                                  <div className={cn(secondaryText, 'text-xs')}>
                                    {staff.restaurantNames.length > 0
                                      ? staff.restaurantNames.join(', ')
                                      : 'No restaurants'}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {!staff.restaurantRole && (
                                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                                      No role
                                    </span>
                                  )}
                                  <select
                                    value={staff.restaurantRole ?? ''}
                                    disabled={updating === roleKey}
                                    onChange={(e) => handleRoleChange(staff.id, e.target.value)}
                                    className={selectClass}
                                  >
                                    <option value="" disabled>
                                      Select a role
                                    </option>
                                    {/* Keep a stored value the frontend list doesn't know visible
                                        instead of rendering an empty dropdown. */}
                                    {staff.restaurantRole &&
                                      !RESTAURANT_ROLES.includes(staff.restaurantRole) && (
                                        <option value={staff.restaurantRole}>
                                          {staff.restaurantRole}
                                        </option>
                                      )}
                                    {RESTAURANT_ROLES.map((role) => (
                                      <option key={role} value={role}>
                                        {role}
                                      </option>
                                    ))}
                                  </select>
                                  <InternalRoleSelect
                                    value={staff.internalRole}
                                    disabled={updating === internalKey}
                                    onChange={(next) => handleInternalRoleChange(staff.id, next)}
                                  />
                                </div>
                              </div>
                              {(rowErrors[roleKey] || rowErrors[internalKey]) && (
                                <div className={cn('mt-2 text-xs', 'text-red-600 dark:text-red-400')}>
                                  {rowErrors[roleKey] || rowErrors[internalKey]}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </AdminLayout>
  );
};

interface InternalRoleSelectProps {
  value: string | null;
  disabled?: boolean;
  onChange: (next: InternalRole | null) => void;
}

const InternalRoleSelect = ({ value, disabled, onChange }: InternalRoleSelectProps) => (
  <select
    value={value ?? ''}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value === '' ? null : (e.target.value as InternalRole))}
    className={selectClass}
  >
    <option value="">Internal role not set</option>
    {INTERNAL_ROLES.map((role) => (
      <option key={role} value={role}>
        {INTERNAL_ROLE_LABELS[role]}
      </option>
    ))}
  </select>
);

export default AdminCommunicationRolesPage;
